import React, { useId } from "react";
import classNames from "classnames";
import { groupBy, orderBy } from "lodash";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Loading from "@components/shared/Loading";
import VolumeBar from "@components/zenith/ui/VolumeBar";
import { useSubscriberLevel0Resources } from "@hooks/query/zenith/useSubscriberLevel0Resources";
import { formatVolume } from "@utils/formatVolume";
import { Volumes, sumVolumes, volumesOf, zoneVolumes } from "@utils/volumes";
import ViewBySwitch, { ViewBy, useViewByLabels } from "./ViewBySwitch";

type Balance = Volumes & { name: string; zoneAccountId?: string };

const AvailableWaterWidget: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const titleId = useId();
  const [viewBy, setViewBy] = React.useState<ViewBy>("level0wrs");
  const { data: zones = [], isLoading } = useSubscriberLevel0Resources({
    select: (res: any[] = []) => orderBy(res, ["identifier"], ["asc"]),
  });

  const balances: Balance[] = React.useMemo(() => {
    switch (viewBy) {
      case "water_class":
        return Object.entries(
          groupBy(
            zones.flatMap((zone: any) => zone.waterClasses),
            "name",
          ),
        )
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([name, waterClasses]) => ({
            name,
            ...sumVolumes(waterClasses.map(volumesOf)),
          }));
      case "summary":
        return [
          {
            name: t("zenith.balances.all_level0wrs"),
            ...sumVolumes(zones.map(zoneVolumes)),
          },
        ];
      default:
        return zones.map((zone: any) => ({
          name: zone.identifier,
          zoneAccountId: zone.id,
          ...zoneVolumes(zone),
        }));
    }
  }, [t, viewBy, zones]);

  // Between accounting periods the API lists the zones with null period figures.
  const noPeriod = zones.some((zone: any) => zone.balance === null);

  const kind = useViewByLabels();

  if (isLoading) {
    return (
      <div className="flex justify-center pt-16">
        <Loading />
      </div>
    );
  }

  return (
    <section aria-labelledby={titleId} className="space-y-3">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <h2 id={titleId} className="text-sm font-bold text-primary-3">
          {t("zenith.balances.available_water")}
        </h2>
        <ViewBySwitch value={viewBy} onChange={setViewBy} />
      </header>
      {/* As many columns as fit, which the info panel narrows. */}
      <ul className="grid grid-cols-fill-72 gap-3.5">
        {balances.map(balance => (
          <BalanceCard
            key={balance.name}
            kind={kind[viewBy]}
            noPeriod={noPeriod}
            {...balance}
          />
        ))}
      </ul>
    </section>
  );
};

/** One group's volumes, in litres; a zone account id adds its link. */
const BalanceCard: React.FunctionComponent<
  Balance & { kind: string; noPeriod: boolean }
> = ({
  kind,
  noPeriod,
  name,
  remaining,
  used,
  allocated,
  traded,
  zoneAccountId,
}) => {
  const { t } = useTranslation();
  const nameId = useId();
  const linkTextId = useId();

  return (
    <li
      aria-labelledby={nameId}
      className="flex flex-col rounded-lg border border-gray-200 bg-white p-5"
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <div className="text-xs text-gray-500">{kind}</div>
          <h3 id={nameId} className="text-xl font-bold text-primary-3">
            {name}
          </h3>
        </div>
      </div>
      {noPeriod ? (
        <p className="text-sm text-gray-500">
          {t("zenith.dashboard.no_active_period")}
        </p>
      ) : (
        <>
          <VolumeBar used={used} total={allocated} />
          <div className="mt-3.5 grid grid-cols-fit-34 gap-2">
            <Figure
              label={t("zenith.balances.available")}
              value={remaining}
              className={remaining < 0 ? "text-red-500" : "text-green-600"}
            />
            <Figure
              label={t("zenith.balances.used")}
              value={used}
              className="text-amber-600"
            />
            {traded ? (
              <Figure
                label={t("common.trades")}
                value={traded}
                className="text-primary-3"
              />
            ) : null}
          </div>
        </>
      )}
      {zoneAccountId ? (
        <Link
          to={`/zenith/zone_accounts/${zoneAccountId}`}
          // Visible text first, then the zone: "View Zone Account CLGW06".
          aria-labelledby={`${linkTextId} ${nameId}`}
          className="mt-3 rounded border border-primary-1 px-3 py-1.5 text-center text-xs font-semibold text-primary-1 hover:bg-blue-50"
        >
          <span id={linkTextId}>
            {t("zenith.balances.view_level0wrs_account")}
          </span>
        </Link>
      ) : null}
    </li>
  );
};

/** A labelled volume in litres; assistive tech reads it as a group named by its label. */
const Figure: React.FunctionComponent<{
  label: string;
  value: number;
  className: string;
}> = ({ label, value, className }) => {
  const labelId = useId();

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className="rounded-lg bg-gray-50 px-3 py-2.5"
    >
      <div id={labelId} className="text-xxs text-gray-500">
        {label}
      </div>
      <div className={classNames("text-lg font-bold", className)}>
        {formatVolume(value)}
      </div>
    </div>
  );
};

export default AvailableWaterWidget;
